<template>
  <div :class="$style.container">
    <v-card :class="$style.formCard">
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
      />
      <v-card-text class="pa-6">
        <v-row>
          <v-col cols="12">
            <div :class="$style.timeRow">
              <div :class="$style.timeField">
                <app-date-time-picker
                  v-model="form.startAt"
                  :label="globalTranslate('dashboardAnnouncementAdmin.startTime') + ' *'"
                  :placeholder="globalTranslate('dashboardAnnouncementAdmin.dateTimePlaceholder')"
                  :invalid-message="globalTranslate('dashboardAnnouncementAdmin.messages.invalidDateTime')"
                  :error-messages="errors.start_at"
                  :disabled="isLoading"
                />
              </div>
              <div :class="$style.timeField">
                <app-date-time-picker
                  v-model="form.endAt"
                  :label="globalTranslate('dashboardAnnouncementAdmin.endTime') + ' *'"
                  :placeholder="globalTranslate('dashboardAnnouncementAdmin.dateTimePlaceholder')"
                  :invalid-message="globalTranslate('dashboardAnnouncementAdmin.messages.invalidDateTime')"
                  :error-messages="errors.end_at"
                  :disabled="isLoading"
                />
              </div>
              <v-checkbox
                v-model="form.isEnabled"
                :class="$style.enabledField"
                :label="globalTranslate('dashboardAnnouncementAdmin.enabled')"
                true-icon="mdi:mdi-checkbox-marked"
                false-icon="mdi:mdi-checkbox-blank-outline"
                color="primary"
                density="compact"
                hide-details
                :disabled="isLoading"
              />
            </div>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="form.title"
              :label="globalTranslate('dashboardAnnouncementAdmin.title') + ' *'"
              variant="outlined"
              density="compact"
              counter="255"
              maxlength="255"
              :error-messages="errors.title"
              :disabled="isLoading"
            />
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="form.body"
              :label="globalTranslate('dashboardAnnouncementAdmin.message') + ' *'"
              variant="outlined"
              density="compact"
              rows="6"
              auto-grow
              counter="5000"
              maxlength="5000"
              :hint="globalTranslate('dashboardAnnouncementAdmin.plainTextHint')"
              persistent-hint
              :error-messages="errors.body"
              :disabled="isLoading"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions class="pa-4">
        <div
          v-if="saved"
          :class="$style.authors"
        >
          <div>
            {{ globalTranslate('dashboardAnnouncementAdmin.createdBy', {
              name: authorName(saved.created_by),
              date: formatTimestamp(saved.created_at),
            }) }}
          </div>
          <div>
            {{ globalTranslate('dashboardAnnouncementAdmin.updatedBy', {
              name: authorName(saved.updated_by),
              date: formatTimestamp(saved.updated_at),
            }) }}
          </div>
        </div>
        <v-spacer />
        <v-btn
          color="primary"
          variant="elevated"
          class="px-5"
          :loading="isSubmitting"
          :disabled="isLoading"
          @click="onSubmit"
        >
          {{ globalTranslate('button.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar
      v-model="snackbar.isShowing"
      :class="$style.snackbar"
      location="top right"
      :timeout="5000"
    >
      <v-alert
        closable
        :type="snackbar.type"
        :color="snackbar.type"
        :icon="snackbar.type === 'success' ? 'mdi:mdi-check-circle' : 'mdi:mdi-alert-circle'"
        close-icon="mdi:mdi-close"
        @click:close="snackbar.isShowing = false"
      >
        {{ snackbar.message }}
      </v-alert>
    </v-snackbar>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue';
import axios from 'axios';
import moment from 'moment';
import AppDateTimePicker from '../../common/shared/components/app-date-time-picker.vue';
import { globalTranslate } from '../../common/shared/locale/app.i18n';

const API_URL = '/api/v2/admin/dashboard-announcement';

/**
 * @typedef {object} AnnouncementAuthor
 * @property {number} id User id.
 * @property {string | null} name Null when the user no longer exists.
 */

/**
 * Announcement as returned by the admin API. Times are Pacific wall-clock strings.
 * @typedef {object} AdminAnnouncement
 * @property {number} id Announcement id.
 * @property {string} title Title.
 * @property {string} body Plain-text message.
 * @property {string} start_at "YYYY-MM-DD HH:mm"
 * @property {string} end_at "YYYY-MM-DD HH:mm"
 * @property {boolean} is_enabled Whether it can show on dashboards.
 * @property {AnnouncementAuthor} created_by Admin who created it.
 * @property {AnnouncementAuthor} updated_by Admin who last saved it.
 * @property {string} created_at "YYYY-MM-DD HH:mm:ss"
 * @property {string} updated_at "YYYY-MM-DD HH:mm:ss"
 */

/** Fields that can carry a validation error, named as in the API. */
const ERROR_FIELDS = /** @type {const} */ (['title', 'body', 'start_at', 'end_at']);

/**
 * @typedef {typeof ERROR_FIELDS[number]} ErrorField
 */

/**
 * Body of a failed API request.
 * @typedef {object} ApiErrorBody
 * @property {string} [message] Error message.
 * @property {Partial<Record<ErrorField, string[]>>} [errors] Laravel validation errors (422).
 */

const isLoading = ref(true);
const isSubmitting = ref(false);

/**
 * Last saved announcement from the API, or null when none has been saved yet.
 * @type {import('vue').Ref<AdminAnnouncement | null>}
 */
const saved = ref(null);

const form = reactive({
  title: '',
  body: '',
  startAt: '',
  endAt: '',
  isEnabled: true,
});

const errors = reactive({
  title: '',
  body: '',
  start_at: '',
  end_at: '',
});

/** @type {import('vue').UnwrapRef<{isShowing: boolean, message: string, type: 'success' | 'error'}>} */
const snackbar = reactive({
  isShowing: false,
  message: '',
  /** @type {'success' | 'error'} */
  type: 'success',
});

/**
 * Show notification.
 * @param {string} message Message.
 * @param {'success'|'error'} type Type.
 */
const showNotification = (message, type = 'success') => {
  snackbar.message = message;
  snackbar.type = type;
  snackbar.isShowing = true;
};

/**
 * Timestamp ("YYYY-MM-DD HH:mm:ss") shown as "MM/DD/YYYY hh:mm AM", the same format as the time inputs.
 * @param {string | null} value Timestamp.
 * @returns {string}
 */
const formatTimestamp = value => {
  const parsed = moment(value, 'YYYY-MM-DD HH:mm:ss', true);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY hh:mm A') : '';
};

/**
 * Author name, or "User #id" when the user no longer exists.
 * @param {AnnouncementAuthor} author Author.
 * @returns {string}
 */
const authorName = author => author.name || globalTranslate('dashboardAnnouncementAdmin.unknownUser', { id: author.id });

/**
 * Fill the form from an API announcement.
 * @param {AdminAnnouncement | null} announcement Announcement.
 */
const fillForm = announcement => {
  saved.value = announcement;
  form.title = announcement?.title ?? '';
  form.body = announcement?.body ?? '';
  form.startAt = announcement?.start_at ?? '';
  form.endAt = announcement?.end_at ?? '';
  form.isEnabled = announcement?.is_enabled ?? true;
};

const clearErrors = () => {
  ERROR_FIELDS.forEach(field => {
    errors[field] = '';
  });
};

/**
 * Status and body of a failed API request (both undefined for a network error).
 * @param {unknown} error Caught error.
 * @returns {{status: number | undefined, data: ApiErrorBody | undefined}}
 */
const errorResponse = error => {
  const response = axios.isAxiosError(error)
    ? /** @type {import('axios').AxiosError<ApiErrorBody>} */ (error).response
    : undefined;
  return { status: response?.status, data: response?.data };
};

/**
 * Required fields and End after Start.
 * @returns {boolean}
 */
const validate = () => {
  clearErrors();
  const required = globalTranslate('dashboardAnnouncementAdmin.messages.required');
  if (!form.title.trim()) errors.title = required;
  if (!form.body.trim()) errors.body = required;
  if (!form.startAt) errors.start_at = required;
  if (!form.endAt) errors.end_at = required;

  // Both are "YYYY-MM-DD HH:mm", so string order is time order.
  if (form.startAt && form.endAt && form.endAt <= form.startAt) {
    errors.end_at = globalTranslate('dashboardAnnouncementAdmin.messages.endAfterStart');
  }

  return Object.values(errors).every(error => !error);
};

const fetchAnnouncement = async () => {
  isLoading.value = true;
  try {
    const response = await axios.get(API_URL);
    fillForm(response.data.data);
  } catch (error) {
    const { data } = errorResponse(error);
    showNotification(data?.message ?? globalTranslate('dashboardAnnouncementAdmin.messages.fetchFailed'), 'error');
  } finally {
    isLoading.value = false;
  }
};

const onSubmit = async () => {
  if (!validate()) return;

  isSubmitting.value = true;
  try {
    const response = await axios.put(API_URL, {
      title: form.title,
      body: form.body,
      start_at: form.startAt,
      end_at: form.endAt,
      is_enabled: form.isEnabled,
    });
    fillForm(response.data.data);
    showNotification(globalTranslate('dashboardAnnouncementAdmin.messages.saveSuccess'));
  } catch (error) {
    const { status, data } = errorResponse(error);
    const fieldErrors = status === 422 ? data?.errors : undefined;
    if (fieldErrors) {
      ERROR_FIELDS.forEach(field => {
        errors[field] = fieldErrors[field]?.[0] ?? '';
      });
    }
    showNotification(data?.message ?? globalTranslate('dashboardAnnouncementAdmin.messages.saveFailed'), 'error');
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(fetchAnnouncement);
</script>

<style module>
.container {
  position: relative;
  /* Avoid hide Operations menu */
  z-index: 0;
  max-width: 960px;
}

.formCard {
  overflow: hidden;
}

.timeRow {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.timeField {
  width: 260px;
}

/* Line the checkbox up with the date inputs. */
.enabledField {
  flex: 0 0 auto;
  margin-top: 2px;
}

/*
 * Let the alert fill the snackbar, as on other admin screens. They get this from base.css
 * (theme/vuetify/v-snackbar.css), which this page does not load; without it the snackbar's
 * dark wrapper shows around the alert.
 */
.snackbar :global(.v-snackbar__content) {
  padding: 0;
}

.authors {
  font-size: 0.875rem;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.6);
}
</style>
